import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { FeedbackModal } from "./FeedbackModal";

interface FeedbackModalContextValue {
  isOpen: boolean;
  openModal: (presetType?: string) => void;
  closeModal: () => void;
}

const FeedbackModalContext = createContext<FeedbackModalContextValue | null>(null);

export function useFeedbackModal() {
  const ctx = useContext(FeedbackModalContext);
  if (!ctx) throw new Error("useFeedbackModal must be used within FeedbackModalProvider");
  return ctx;
}

export function FeedbackModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [presetType, setPresetType] = useState<string | undefined>();

  const openModal = useCallback((type?: string) => {
    setPresetType(type);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setPresetType(undefined);
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <FeedbackModalContext.Provider value={{ isOpen, openModal, closeModal }}>
      {children}
      {isOpen &&
        createPortal(
          <FeedbackModal onClose={closeModal} presetType={presetType} />,
          document.body
        )}
    </FeedbackModalContext.Provider>
  );
}
